/**
 * CSV Exporter
 * Handles CSV generation and download for analytics data
 * 
 * Extends the existing export-to-csv pattern used in ModelManager.tsx
 */

import { mkConfig, generateCsv, download } from "export-to-csv";
import moment from "moment";

import { ExportableData, CsvExportOptions, ExportColumn } from "./types";
import Logger from "../../utils/Logger";

/**
 * Default CSV export options
 */
const DEFAULT_CSV_OPTIONS: CsvExportOptions = {
    includeHeaders: true,
    delimiter: ",",
    quoteStrings: true
};

/**
 * Format a cell value based on column configuration
 */
function formatCellValue(value: unknown, column: ExportColumn): string | number {
    if (value === null || value === undefined) {
        return "";
    }

    switch (column.format) {
        case "number":
            return typeof value === "number" ? value : Number(value) || 0;
        case "percent": {
            const numValue = typeof value === "number" ? value : Number(value) || 0;
            return `${numValue.toFixed(2)}%`;
        }
        case "date":
            return moment(String(value)).format("YYYY-MM-DD");
        case "text":
        default:
            return String(value);
    }
}

/**
 * Transform exportable data rows to use display labels as keys
 */
function transformRows(data: ExportableData): Record<string, unknown>[] {
    return data.rows.map(row => {
        const transformedRow: Record<string, unknown> = {};
        
        data.columns.forEach(column => {
            const displayLabel = column.label;
            const value = row[column.key];
            transformedRow[displayLabel] = formatCellValue(value, column);
        });

        return transformedRow;
    });
}

/**
 * Generate a filename for the CSV export
 */
function generateFilename(data: ExportableData, customFilename?: string): string {
    if (customFilename) {
        // Remove .csv extension if provided - export-to-csv adds it
        return customFilename.replace(/\.csv$/i, "");
    }

    const timestamp = moment().format("YYYYMMDD_HHmmss");
    const titleSlug = data.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");

    const customerSlug = data.metadata?.customerSlug || "export";

    return `${titleSlug}_${customerSlug}_${timestamp}`;
}

/**
 * Export data to CSV and trigger download
 */
export function exportToCsv(
    data: ExportableData,
    options: CsvExportOptions = {}
): void {
    const mergedOptions = { ...DEFAULT_CSV_OPTIONS, ...options };

    try {
        const rows = transformRows(data);

        if (rows.length === 0) {
            Logger.log("warn", "CSV Export", "No data to export");
            return;
        }

        const csvConfig = mkConfig({
            useKeysAsHeaders: mergedOptions.includeHeaders,
            filename: generateFilename(data, mergedOptions.filename),
            fieldSeparator: mergedOptions.delimiter,
            quoteStrings: mergedOptions.quoteStrings
        });

        const csv = generateCsv(csvConfig)(rows);
        download(csvConfig)(csv);

        Logger.log("info", "CSV Export", `Exported ${rows.length} rows to CSV`);
    } catch (error) {
        Logger.log("error", "CSV Export", "Failed to export CSV", error);
        throw new Error("Failed to export data to CSV");
    }
}

/**
 * Export multiple data tables to separate CSV files
 */
export function exportMultipleToCsv(
    datasets: ExportableData[],
    options: CsvExportOptions = {}
): void {
    datasets.forEach((data, index) => {
        const filenamePrefix = options.filename 
            ? `${options.filename}_${index + 1}` 
            : undefined;
        
        exportToCsv(data, { ...options, filename: filenamePrefix });
    });
}

/**
 * Generate CSV string without downloading (useful for previews or server-side)
 */
export function generateCsvString(
    data: ExportableData,
    options: CsvExportOptions = {}
): string {
    const mergedOptions = { ...DEFAULT_CSV_OPTIONS, ...options };
    const rows = transformRows(data);

    if (rows.length === 0) {
        return "";
    }

    const csvConfig = mkConfig({
        useKeysAsHeaders: mergedOptions.includeHeaders,
        fieldSeparator: mergedOptions.delimiter,
        quoteStrings: mergedOptions.quoteStrings
    });

    const csv = generateCsv(csvConfig)(rows);
    
    // generateCsv returns a CsvOutput object, we need to convert to string
    return csv.toString();
}
